export interface SparklineProps {
  /** Up to 7 values (number or null). Null values represent missing days / empty slots. */
  values: Array<number | null>
  /** Accessible label describing the trend (e.g., "Quota attainment over last 7 days: 57%") */
  ariaLabel: string
  /** Height in pixels (default 24) */
  height?: number
  /** Bar width in pixels (default 5) */
  barWidth?: number
  /** Gap between bars in pixels (default 3) */
  gap?: number
  /** Optional explicit max scale (defaults to highest non-null value, minimum 1) */
  max?: number
  className?: string
  testId?: string
}

const TOTAL_SLOTS = 7

/**
 * Sparkline — compact 7-bar trend indicator for Helper Me and stock analytics.
 * The 7th bar is solid accent, preceding bars are muted, and null entries render as empty slots.
 */
export function Sparkline({
  values,
  ariaLabel,
  height = 24,
  barWidth = 5,
  gap = 3,
  max,
  className,
  testId,
}: SparklineProps) {
  // Ensure we display exactly 7 slots, taking the last 7 if more are provided,
  // or padding with null at the start if fewer are provided.
  let slots: Array<number | null> = values.slice(-TOTAL_SLOTS)
  if (slots.length < TOTAL_SLOTS) {
    const padCount = TOTAL_SLOTS - slots.length
    slots = [...Array(padCount).fill(null), ...slots]
  }

  const validNumbers = slots.filter((v): v is number => v !== null && Number.isFinite(v))
  const scaleMax = max ?? (validNumbers.length > 0 ? Math.max(...validNumbers, 1) : 1)
  const totalWidth = TOTAL_SLOTS * barWidth + (TOTAL_SLOTS - 1) * gap

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      width={totalWidth}
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      className={`inline-block flex-none overflow-visible select-none ${className ?? ''}`}
    >
      {slots.map((val, index) => {
        const x = index * (barWidth + gap)
        const isLast = index === TOTAL_SLOTS - 1

        if (val === null || !Number.isFinite(val)) {
          // Empty slot rendered as a subtle resting baseline tick
          return (
            <rect
              key={index}
              data-slot-index={index}
              data-empty="true"
              x={x}
              y={height - 3}
              width={barWidth}
              height={2}
              rx={1}
              className="fill-[var(--border-strong)] opacity-40"
            />
          )
        }

        const clamped = Math.max(0, val)
        const ratio = scaleMax > 0 ? Math.min(clamped / scaleMax, 1) : 0
        const barHeight = Math.max(Math.round(ratio * (height - 3)), 3)
        const y = height - barHeight

        return (
          <rect
            key={index}
            data-slot-index={index}
            data-value={val}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            rx={1.5}
            className={
              isLast
                ? 'fill-[var(--accent)]'
                : 'fill-[color-mix(in_srgb,var(--accent)_38%,transparent)]'
            }
          />
        )
      })}
    </svg>
  )
}
